import {
  FETCH_VOLUME_PERFORMANCE_REPORT,
  REQUEST_POST,
} from '/enum/request.enum';
import {request} from '/redux/api/request';

import {
  VolumePerformanceFilters,
  VolumePerformanceUser,
} from '../../component/screen/report/VolumePerformanceReport/types/volumePerformanceReport.types';

export const SET_IS_LIST_LOADING =
  'VOLUME_PERFORMANCE_REPORT/SET_IS_LIST_LOADING';
export const GET_VOLUME_PERFORMANCE_USER_LIST =
  'VOLUME_PERFORMANCE_REPORT/GET_VOLUME_PERFORMANCE_USER_LIST';
export const SET_IS_FETCHING_NEXT_PAGE =
  'VOLUME_PERFORMANCE_REPORT/SET_IS_FETCHING_NEXT_PAGE';
export const SET_IS_END_REACHED =
  'VOLUME_PERFORMANCE_REPORT/SET_IS_END_REACHED';
export const SET_RESULTS_COUNT = 'VOLUME_PERFORMANCE_REPORT/SET_RESULTS_COUNT';
export const SET_FILTERS = 'VOLUME_PERFORMANCE_REPORT/SET_FILTERS';
export const SET_SORT = 'VOLUME_PERFORMANCE_REPORT/SET_SORT';
export const RESET_REPORT = 'VOLUME_PERFORMANCE_REPORT/RESET_REPORT';

export const setIsListLoading = (isLoading: boolean) => ({
  type: SET_IS_LIST_LOADING,
  payload: isLoading,
});

export const setIsFetchingNextPage = (isFetching: boolean) => ({
  type: SET_IS_FETCHING_NEXT_PAGE,
  payload: isFetching,
});

export const setIsEndReached = (isEndReached: boolean) => ({
  type: SET_IS_END_REACHED,
  payload: isEndReached,
});

export const setResultsCount = (count: number) => ({
  type: SET_RESULTS_COUNT,
  payload: count,
});

export const setFilters = (filters: VolumePerformanceFilters) => ({
  type: SET_FILTERS,
  payload: filters,
});

export const setSort = (
  sortBy: 'orderDate' | 'amount',
  sortOrder: 'ASC' | 'DESC',
) => ({
  type: SET_SORT,
  payload: {sortBy, sortOrder},
});

export const resetReport = () => ({
  type: RESET_REPORT,
});

export const getVolumePerformanceUserList = (
  data: VolumePerformanceUser[],
  append: boolean = false,
) => ({
  type: GET_VOLUME_PERFORMANCE_USER_LIST,
  payload: {data, append},
});

/**
 * Fetch volume performance user list with pagination support from the API.
 * @param page - The page number to fetch.
 * @param id - The user ID to fetch volume performance for user.
 * @param search - The search string for the user list.
 * @param filters - Optional filters for the report. If not provided, it will use filters from the state.
 */
export const fetchVolumePerformanceUserList =
  (
    page: number = 1,
    id: number,
    search?: string,
    filters?: VolumePerformanceFilters,
  ) =>
  async (dispatch: any, getState: any) => {
    const {
      isFetchingNextPage,
      isEndReached,
      filters: stateFilters,
      sort,
    } = getState().volumePerformanceReport;

    // Prevent simultaneous fetches for the same page or next page,
    // but ALWAYS allow page 1 to proceed (this is a reset/sort/filter change)
    if (page > 1 && (isFetchingNextPage || isEndReached)) {
      return;
    }

    if (page === 1) {
      dispatch(setIsListLoading(true));
      dispatch(setIsEndReached(false)); // Reset end reached on fresh load
      dispatch(setResultsCount(0)); // Reset results count on fresh load
    } else {
      dispatch(setIsFetchingNextPage(true));
    }

    try {
      const appliedFilters = filters || stateFilters;

      const {data, responseHeaders}: any = await request(
        FETCH_VOLUME_PERFORMANCE_REPORT,
        {
          method: REQUEST_POST,
          body: {
            id: Number(id),
            ...(search && {search}),
            ...(appliedFilters && {filters: appliedFilters}),
          },
          queryParams: {
            sortBy: sort.sortBy,
            sortOrder: sort.sortOrder,
            pageNumber: page,
            pageSize: 10,
          },
          getHeaders: ['Report-Count'],
        },
      );

      if (responseHeaders && responseHeaders[0]) {
        dispatch(setResultsCount(parseInt(responseHeaders[0], 10)));
      }

      if (data) {
        dispatch(getVolumePerformanceUserList(data, page > 1));

        // Detect end reached if response returns fewer items than the page size
        if (data.length < 10) {
          dispatch(setIsEndReached(true));
        }
      } else {
        dispatch(setIsEndReached(true));
      }
    } catch (error) {
      // Clear data and reset count on failure to avoid showing stale results
      dispatch(getVolumePerformanceUserList([], false));
      dispatch(setResultsCount(0));
      dispatch(setIsEndReached(true));
    } finally {
      if (page === 1) {
        dispatch(setIsListLoading(false));
      } else {
        dispatch(setIsFetchingNextPage(false));
      }
    }
  };
